// WP-1.19 (OB-1, S-04 `/onboarding`): the first-run wizard. 7 steps with progress dots; ①②
// required, ③④⑤⑥⑦ functional (WP-2.1 enabled ⑤ WhatsApp, WP-C1.1 enabled ⑥ Calendars). Every
// write goes through the normal engine/API paths (accounts, category seed, settings);
// finishing sets `onboarded=true`, which the auth-status gate reads.
import { CURRENCIES, parseAmountInput, type AccountType, type CurrencyCode } from '@pfos/engine';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { CalendarConnectPanel } from '../components/CalendarConnectPanel';
import { WhatsAppLinkPanel } from '../components/WhatsAppLinkPanel';
import { useAccounts, useCreateAccount } from '../lib/accounts';
import { useCategories, useSeedDefaultCategories } from '../lib/categories';
import { useSetSetting } from '../lib/settings';

const TOTAL_STEPS = 7;

// §19.1 account seed — pre-filled for one-tap creation. All start at ~0; the owner sets openings.
interface SeedAccount {
  name: string;
  type: AccountType;
  currency: CurrencyCode;
  platformGroup: string;
}
const SEED_ACCOUNTS: readonly SeedAccount[] = [
  { name: 'Meezan Bank', type: 'bank', currency: 'PKR', platformGroup: 'PKR banks' },
  { name: 'UBL Bank', type: 'bank', currency: 'PKR', platformGroup: 'PKR banks' },
  { name: 'BankIslami', type: 'bank', currency: 'PKR', platformGroup: 'PKR banks' },
  { name: 'Easypaisa', type: 'mobile_wallet', currency: 'PKR', platformGroup: 'PKR wallets' },
  { name: 'JazzCash', type: 'mobile_wallet', currency: 'PKR', platformGroup: 'PKR wallets' },
  { name: 'NayaPay', type: 'mobile_wallet', currency: 'PKR', platformGroup: 'PKR wallets' },
  { name: 'Cash', type: 'cash', currency: 'PKR', platformGroup: 'Cash' },
  { name: 'PayPal', type: 'psp_wallet', currency: 'USD', platformGroup: 'USD' },
  { name: 'Wise', type: 'psp_wallet', currency: 'USD', platformGroup: 'USD' },
  { name: 'Payoneer', type: 'psp_wallet', currency: 'USD', platformGroup: 'USD' },
  { name: 'Skrill', type: 'psp_wallet', currency: 'USD', platformGroup: 'USD' },
  { name: 'RedotPay', type: 'psp_wallet', currency: 'USD', platformGroup: 'USD' },
  { name: 'Nsave', type: 'psp_wallet', currency: 'USD', platformGroup: 'USD' },
];

const NOTIFICATION_TYPES = [
  'Schedule item due / overdue',
  'Payable due / overdue',
  'Recurring bill needs confirming',
  'Low Spendable-Now threshold crossed',
  'Backup failed',
];

function ProgressDots({ step }: { step: number }): React.JSX.Element {
  return (
    <div
      className="mb-6 flex justify-center gap-2"
      aria-label={`Step ${String(step)} of ${String(TOTAL_STEPS)}`}
    >
      {Array.from({ length: TOTAL_STEPS }, (_, i) => (
        <span
          key={i}
          className={`h-2 w-2 rounded-full ${i + 1 === step ? 'bg-primary' : i + 1 < step ? 'bg-primary/40' : 'bg-border'}`}
        />
      ))}
    </div>
  );
}

export function OnboardingPage(): React.JSX.Element {
  const navigate = useNavigate();
  const setSetting = useSetSetting();
  const { data: accounts } = useAccounts();
  const createAccount = useCreateAccount();
  const { data: categories } = useCategories({});
  const seedCategories = useSeedDefaultCategories();

  const [step, setStep] = useState(1);
  const [baseCurrency, setBaseCurrency] = useState<CurrencyCode>('PKR');
  const [fiscalYearStart, setFiscalYearStart] = useState<'jan' | 'jul'>('jan');
  const [openings, setOpenings] = useState<Record<string, string>>({});
  const [asOf, setAsOf] = useState('2026-01-01');
  const [minReserve, setMinReserve] = useState('');
  const [finishing, setFinishing] = useState(false);

  const existingNames = new Set((accounts ?? []).map((a) => a.name));
  const asOfIso = new Date(asOf).toISOString();

  const next = (): void => {
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const back = (): void => {
    setStep((s) => Math.max(1, s - 1));
  };

  const step1Next = (): void => {
    setSetting.mutate({ key: 'baseCurrency', value: baseCurrency });
    setSetting.mutate({ key: 'fiscalYearStart', value: fiscalYearStart });
    next();
  };

  const createSeed = (seed: SeedAccount): void => {
    const raw = openings[seed.name] ?? '';
    const parsed =
      raw.trim() === '' ? 0 : parseAmountInput(raw, { defaultCurrency: seed.currency });
    const openingMinor = typeof parsed === 'number' ? 0 : parsed.ok ? parsed.money.minorUnits : 0;
    createAccount.mutate({
      name: seed.name,
      type: seed.type,
      currency: seed.currency,
      openingMinor,
      openingAsOf: asOfIso,
      platformGroup: seed.platformGroup,
    });
  };

  const finish = (): void => {
    setFinishing(true);
    if (minReserve.trim() !== '') {
      const parsed = parseAmountInput(minReserve, { defaultCurrency: baseCurrency });
      if (parsed.ok)
        setSetting.mutate({ key: 'minimumReserveMinor', value: parsed.money.minorUnits });
    }
    setSetting.mutate(
      { key: 'onboarded', value: true },
      {
        onSuccess: () => {
          navigate('/');
        },
      },
    );
  };

  return (
    <div className="mx-auto flex min-h-full max-w-xl flex-col justify-center px-4 py-10">
      <h1 className="mb-1 text-center text-2xl font-semibold text-ink">Welcome to PF-OS</h1>
      <p className="mb-6 text-center text-sm text-muted">A few quick steps to set things up.</p>
      <ProgressDots step={step} />

      <div className="rounded-[var(--radius-card)] border border-border bg-surface p-6">
        {step === 1 && (
          <div>
            <h2 className="mb-4 text-lg font-medium text-ink">Base currency &amp; year</h2>
            <label className="mb-3 block text-sm text-muted" htmlFor="ob-currency">
              Base currency
            </label>
            <select
              id="ob-currency"
              className={selectClass}
              value={baseCurrency}
              onChange={(e) => {
                setBaseCurrency(e.target.value as CurrencyCode);
              }}
            >
              {Object.keys(CURRENCIES).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <p className="mb-2 mt-4 text-sm text-muted">Reporting year</p>
            <div className="flex gap-2" role="radiogroup" aria-label="Reporting year">
              {[
                { value: 'jan', label: 'Calendar (Jan–Dec)' },
                { value: 'jul', label: 'Fiscal (Jul–Jun)' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={fiscalYearStart === opt.value}
                  onClick={() => {
                    setFiscalYearStart(opt.value as 'jan' | 'jul');
                  }}
                  className={`flex-1 rounded-[var(--radius-control)] border px-3 py-2 text-sm ${
                    fiscalYearStart === opt.value
                      ? 'border-primary bg-primary/10 font-medium text-primary'
                      : 'border-border text-ink hover:bg-bg'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="mb-1 text-lg font-medium text-ink">Accounts &amp; opening balances</h2>
            <p className="mb-4 text-xs text-muted">
              Tap to create each account you use, with the balance you have right now.
            </p>
            <label className="mb-3 block text-sm text-muted" htmlFor="ob-asof">
              As-of date
              <input
                id="ob-asof"
                type="date"
                className={`${inputClass} mt-1`}
                value={asOf}
                onChange={(e) => {
                  setAsOf(e.target.value);
                }}
              />
            </label>
            <ul className="space-y-2">
              {SEED_ACCOUNTS.map((seed) => {
                const created = existingNames.has(seed.name);
                return (
                  <li key={seed.name} className="flex items-center gap-2">
                    <span className="w-28 shrink-0 text-sm text-ink">{seed.name}</span>
                    <span className="w-10 shrink-0 text-xs text-muted">{seed.currency}</span>
                    <input
                      className={`${inputClass} flex-1`}
                      aria-label={`${seed.name} opening balance`}
                      placeholder="Opening balance"
                      disabled={created}
                      value={openings[seed.name] ?? ''}
                      onChange={(e) => {
                        setOpenings((o) => ({ ...o, [seed.name]: e.target.value }));
                      }}
                    />
                    {created ? (
                      <span className="flex w-24 shrink-0 items-center justify-center gap-1 text-sm text-success">
                        <Check size={14} /> Added
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={`${ghostButtonInlineClass} w-24 shrink-0`}
                        disabled={createAccount.isPending}
                        onClick={() => {
                          createSeed(seed);
                        }}
                      >
                        Create
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="mb-1 text-lg font-medium text-ink">Category tree</h2>
            <p className="mb-4 text-xs text-muted">
              A ready-made category tree (§16.11) you can tweak later in Settings.
            </p>
            {(categories ?? []).length === 0 ? (
              <button
                type="button"
                className={primaryButtonInlineClass}
                disabled={seedCategories.isPending}
                onClick={() => {
                  seedCategories.mutate();
                }}
              >
                Add the default categories
              </button>
            ) : (
              <p className="text-sm text-success">{(categories ?? []).length} categories ready.</p>
            )}
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="mb-1 text-lg font-medium text-ink">Minimum reserve</h2>
            <p className="mb-4 text-xs text-muted">
              An amount to keep aside — PF-OS warns you before Spendable Now dips below it.
              Optional.
            </p>
            <input
              className={inputClass}
              aria-label="Minimum reserve"
              placeholder={`e.g. ${baseCurrency} 50000`}
              value={minReserve}
              onChange={(e) => {
                setMinReserve(e.target.value);
              }}
            />
          </div>
        )}

        {step === 5 && (
          <div>
            {/* WP-2.1: step ⑤ enabled — the same link flow as Settings → WhatsApp ("later" allowed). */}
            <h2 className="mb-1 text-lg font-medium text-ink">WhatsApp</h2>
            <p className="mb-3 text-xs text-muted">
              Link the assistant number to capture expenses by message or voice. You can also do
              this any time from Settings → WhatsApp.
            </p>
            <WhatsAppLinkPanel />
          </div>
        )}

        {step === 6 && (
          <div>
            {/* WP-C1.1: step ⑥ enabled — the same connect flow as Settings → Calendars ("later" allowed). */}
            <h2 className="mb-1 text-lg font-medium text-ink">Calendars</h2>
            <p className="mb-3 text-xs text-muted">
              Optional — connect a calendar to see availability alongside your money. You can also
              do this any time from Settings → Calendars.
            </p>
            <CalendarConnectPanel />
          </div>
        )}

        {step === 7 && (
          <div>
            <h2 className="mb-1 text-lg font-medium text-ink">Notifications</h2>
            <p className="mb-3 text-xs text-muted">You'll be notified about:</p>
            <ul className="mb-3 space-y-1 text-sm text-ink">
              {NOTIFICATION_TYPES.map((n) => (
                <li key={n} className="flex items-center gap-2">
                  <Check size={14} className="text-success" /> {n}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted">
              Quiet hours 23:00–08:00. You can change all of this in Settings.
            </p>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={step === 1}
            onClick={back}
          >
            Back
          </button>
          <div className="flex gap-2">
            {step >= 3 && step < TOTAL_STEPS && (
              <button type="button" className={ghostButtonInlineClass} onClick={next}>
                Skip
              </button>
            )}
            {step === 1 && (
              <button type="button" className={primaryButtonInlineClass} onClick={step1Next}>
                Next
              </button>
            )}
            {step === 2 && (
              <button
                type="button"
                className={primaryButtonInlineClass}
                disabled={(accounts ?? []).length === 0}
                onClick={next}
              >
                Next
              </button>
            )}
            {step >= 3 && step < TOTAL_STEPS && (
              <button type="button" className={primaryButtonInlineClass} onClick={next}>
                Next
              </button>
            )}
            {step === TOTAL_STEPS && (
              <button
                type="button"
                className={primaryButtonInlineClass}
                disabled={finishing}
                onClick={finish}
              >
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
